const CHAPTER_TYPES = {
  Novel: { key: 'chapters', item: 'chapter', maxItems: 10 },
  Book: { key: 'chapters', item: 'chapter', maxItems: 10 },
  Story: { key: 'chapters', item: 'chapter', maxItems: 10 },
};

const SCENE_TYPES = {
  Script: { key: 'scenes', item: 'scene', maxItems: 10 },
};

export const createPost = async (postData, apiCall) => {
  try {
    const response = await apiCall('/api/posts/create', {
      method: 'POST',
      body: JSON.stringify(postData),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok || !data.success) {
      const errorMessage = data.errors?.[0]?.message || data.message || `Error: ${response.status}`;
      throw new Error(errorMessage);
    }

    return {
      success: true,
      data: data.data,
      post: data.data?.post
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Failed to create post. Please try again.'
    };
  }
};

export const createDraft = async (postData, apiCall) => {
  try {
    const response = await apiCall('/api/drafts/create', {
      method: 'POST',
      body: JSON.stringify(postData),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok || !data.success) {
      const errorMessage = data.errors?.[0]?.message || data.message || `Error: ${response.status}`;
      throw new Error(errorMessage);
    }

    return {
      success: true,
      data: data.data,
      post: data.data?.draft
    };
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.error('Create draft error:', error.message);
    }
    
    return {
      success: false,
      error: error.message || 'Failed to save draft. Please try again.'
    };
  }
};

export const isChapterType = (postType) => !!CHAPTER_TYPES[postType];
export const isSceneType = (postType) => !!SCENE_TYPES[postType];
export const isStructuredContentType = (postType) => isChapterType(postType) || isSceneType(postType);

// Consolidated helper for structured content (Chapters/Scenes)
const prepareStructuredItems = (titles, contents) => {
  return titles
    .map((title, i) => ({
      number: i + 1,
      title: title?.trim(),
      content: contents[i]?.trim()
    }))
    .filter(item => item.title && item.content);
};

export const preparePostData = (formState) => {
  const baseData = {
    title: formState.title.trim(),
    type: formState.postType,
    tags: formState.tags,
    backgroundStyle: formState.backgroundOption,
    fontStyle: formState.selectedFont,
    colorStyle: formState.selectedColor,
  };

  if (isChapterType(formState.postType)) {
    return {
      ...baseData,
      chapters: prepareStructuredItems(formState.chapterTitles, formState.chapterContents).map(item => ({
        chapterNumber: item.number,
        title: item.title,
        content: item.content
      })),
    };
  }

  if (isSceneType(formState.postType)) {
    return {
      ...baseData,
      scenes: prepareStructuredItems(formState.sceneTitles, formState.sceneContents).map(item => ({
        sceneNumber: item.number,
        title: item.title,
        content: item.content
      })),
    };
  }

  return { ...baseData, content: formState.content.trim() };
};

export const getPostTypeConfig = (postType) => {
  if (CHAPTER_TYPES[postType]) return { ...CHAPTER_TYPES[postType], type: 'chapters' };
  if (SCENE_TYPES[postType]) return { ...SCENE_TYPES[postType], type: 'scenes' };
  return { type: 'content' };
};

export const fetchUserPosts = async (apiCall, userId, limit = 10, cursor = null) => {
  try {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (cursor) params.append('cursor', cursor);

    const response = await apiCall(`/api/posts/user/${userId}?${params.toString()}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || `HTTP error! status: ${response.status}`);
    }

    return {
      posts: data.posts || [],
      nextCursor: data.nextCursor || null,
      hasMore: data.hasMore || false
    };
  } catch (error) {
    throw error;
  }
};

export const fetchUserPostsByType = async (apiCall, profileID, type, limit = 10, cursor = null, isFirst = false) => {
  try {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (cursor) params.append('cursor', cursor);
    if (type && type !== 'all') params.append('type', type);
    
    if (isFirst) params.append('isFirst', 'true');

    const response = await apiCall(`/api/posts/profile/${profileID}/type?${params.toString()}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || `HTTP error! status: ${response.status}`);
    }

    return {
      posts: data.posts || [],
      nextCursor: data.nextCursor || null,
      hasMore: data.hasMore || false
    };
  } catch (error) {
    throw error;
  }
};

// Fetch feed posts (for following users)
export const fetchFeedPosts = async (apiCall, feedType = 'for_you', limit = 10, cursor = null) => {
  try {
    const params = new URLSearchParams({ 
      type: feedType,
      limit: limit.toString() 
    });
    if (cursor) params.append('cursor', cursor);
    
    const response = await apiCall(`/api/posts/feed?${params.toString()}`);
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || `HTTP error! status: ${response.status}`);
    }
    
    return {
      posts: data.posts || [],
      nextCursor: data.nextCursor || null,
      hasMore: data.hasMore || false
    };
  } catch (error) {
    throw error;
  }
};

export const fetchReviewRequests = async (apiCall, cursor = null) => {
  try {
    const params = new URLSearchParams();
    if (cursor) params.append('cursor', cursor);

    const response = await apiCall(`/api/drafts/review-requests?${params.toString()}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || `HTTP error! status: ${response.status}`);
    }

    return {
      posts: data.posts || [],
      nextCursor: data.nextCursor || null,
      hasMore: data.hasMore || false
    };
  } catch (error) {
    throw error;
  }
};

export const togglePostLike = async (apiCall, postId, action) => {
  try {
    const response = await apiCall(`/api/likes/${postId}`, {
      method: 'POST',
      body: JSON.stringify({
        action: action 
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || `HTTP error! status: ${response.status}`);
    }

    return {
      success: true,
      postId: postId,
      action: action
    };
  } catch (error) {
    throw error;
  }
};

export const getPostLikes = async (apiCall, postId, limit = 10, cursor = null) => {
  try {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (cursor) params.append('cursor', cursor);

    const response = await apiCall(`/api/likes/${postId}/likes?${params.toString()}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || `HTTP error! status: ${response.status}`);
    }

    return {
      likes: data.data.likes || [],
      nextCursor: data.data.nextCursor || null,
      hasMore: data.data.hasMore || false
    };
  } catch (error) {
    throw error;
  }
};
// Fetch explore posts
export const fetchExplorePosts = async (
  apiCall,
  limit = 10,
  trendingCursor = null,
  randomCursor = null,
  seenPostIds = []
) => {
  try {
    // Build query string properly
    const params = new URLSearchParams();
    params.append('limit', String(limit)); 
    
    if (trendingCursor) {
      params.append('trendingCursor', trendingCursor);
    }
    if (randomCursor) {
      params.append('randomCursor', randomCursor);
    }
    if (Array.isArray(seenPostIds) && seenPostIds.length > 0) {
      params.append('seenPostIds', JSON.stringify(seenPostIds));
    }

    const url = `/api/posts/explore?${params.toString()}`;

    const response = await apiCall(url);
    
    if (!response.ok) {
      const errorText = await response.text();
      const error = new Error(errorText || `HTTP error! status: ${response.status}`);
      error.status = response.status;
      throw error;
    }

    const data = await response.json();

    const currentPagePostIds = (data.posts || []).map(post => post._id.toString());
    const updatedSeenPostIds = [...new Set([
      ...currentPagePostIds,      
      ...seenPostIds              
    ])].slice(0, 150);

    return {
      posts: data.posts || [],
      cursors: {
        trendingCursor: data.cursors?.trendingCursor || null,
        randomCursor: data.cursors?.randomCursor || null,
      },
      _seenPostIds: updatedSeenPostIds, 
      hasMore: data.hasMore !== false
    };
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.error('Error fetching explore posts:', error);
    }
    
    throw error;
  }
};

// Function to fetch trending posts
export const fetchTrendingPosts = async (apiCall, limit = 10, cursor = null) => {
  try {
    const params = new URLSearchParams({ 
      limit: limit.toString() 
    });
    if (cursor) params.append('cursor', cursor);
    
    const response = await apiCall(`/api/posts/trending?${params.toString()}`);
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || `HTTP error! status: ${response.status}`);
    }
    
    return {
      posts: data.posts || [],
      nextCursor: data.nextCursor || null,
      hasMore: data.hasMore || false
    };
  } catch (error) {
    throw error;
  }
};

export const deletePost = async (apiCall, postId) => {
  try {
    const response = await apiCall(`/api/posts/${postId}`, {
      method: 'DELETE'
    });
    
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data.message || `HTTP error! status: ${response.status}`);
    }
    
    return data;
  } catch (error) {
    throw error;
  }
};